import { useState } from 'react';
import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { progressApi, type HomeworkStatus, type HomeworkSubmission } from '@/api/progress';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Pill } from '@/components/ui/Pill';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { humanizeError } from '@/lib/errors';
import { isAudioFile } from '@/components/media/RecordedAudio';
import { AudioPlayer } from '@/components/ui/AudioPlayer';
import { splitHomeworkContent } from '@/lib/homeworkContent';
import { HOMEWORK_PAGE, flattenQueue, nextInQueue, nextQueueOffset } from '@/lib/homeworkQueue';
import { SearchInput } from '@/components/ui/SearchInput';
import { useIsMobile } from '@/hooks/useIsMobile';
import { MBlock, MEmpty, MIcon, MRow, MSegmented, MTag, MTitle } from '@/components/mobile/MobileKit';

/**
 * Превью текста сдачи в списке.
 *
 * ⚠️ Запись голоса из урока сервер кладёт в текст сдачи ссылкой — у 1357 сдач
 * из 2169 текст и есть одна ссылка. В списке вместо голого адреса — «Голосовой
 * ответ» со значком; сама запись играет в окне проверки.
 */
function HomeworkPreview({ content }: { content: string }) {
  const { t } = useTranslation();
  const body = splitHomeworkContent(content);
  return (
    <>
      {body.text && <p className="mt-2 text-sm text-on-surface line-clamp-2">{body.text}</p>}
      {body.audio.length > 0 && (
        <p className="mt-2 flex items-center gap-1.5 text-sm text-on-surface">
          <span className="material-symbols-outlined text-base text-primary" aria-hidden="true">mic</span>
          {t('curatorHomework.voiceAnswer')}
        </p>
      )}
    </>
  );
}

function useStatusLabels() {
  const { t } = useTranslation();
  return {
    pending: t('curatorHomework.statusPending'),
    revision: t('curatorHomework.statusRevision'),
    approved: t('curatorHomework.statusApproved'),
    rejected: t('curatorHomework.statusRejected'),
  } satisfies Record<HomeworkStatus, string>;
}

function statusVariant(s: HomeworkStatus): 'warning' | 'success' | 'danger' | 'neutral' {
  if (s === 'approved') return 'success';
  if (s === 'rejected') return 'danger';
  if (s === 'revision') return 'warning';
  return 'neutral';
}

export function CuratorHomeworkPage() {
  const { t } = useTranslation();
  const STATUS_LABELS = useStatusLabels();
  const [filter, setFilter] = useState<HomeworkStatus | undefined>('pending');
  const [q, setQ] = useState('');
  // ⚠️ СТРАНИЦЫ ПО 50 И ПОИСК НА СЕРВЕРЕ (28.09.2026, разбор удобства админки №3).
  // Раньше грузились первые 20 из 2172 сдач, остальные были недостижимы. Поиск —
  // по всей очереди (ученик, почта, раздел, урок, курс), не по загруженной странице.
  const list = useInfiniteQuery({
    queryKey: ['curator', 'homework', filter, q],
    queryFn: ({ pageParam }) => progressApi.listPendingHomework(filter, pageParam, HOMEWORK_PAGE, q),
    initialPageParam: 0,
    getNextPageParam: (_last, pages) => nextQueueOffset(pages),
  });
  const items = flattenQueue(list.data?.pages ?? []);
  const total = list.data?.pages[list.data.pages.length - 1]?.total ?? 0;
  const [active, setActive] = useState<HomeworkSubmission | null>(null);

  // ⚠️ ИЗ УВЕДОМЛЕНИЯ — СРАЗУ К СДАЧЕ (договор homework-resubmit.md §4.1, решение
  // владельца 11.09.2026). Адрес `?submission=<id>` открывает окно проверки этой
  // работы. Нет её в загруженных страницах (очередь длинная, работа уже проверена
  // или в другой вкладке статуса) — окно открывается по детали с сервера; сервер
  // отказал — об этом строка над списком, а не молчание (28.09.2026).
  //
  // Без эффекта: окно выводится из адреса и списка. Закрытие убирает параметр,
  // иначе «назад» или обновление страницы открывали бы ту же работу снова.
  const [params, setParams] = useSearchParams();
  const linkedId = params.get('submission');
  const inList = linkedId ? (items.find((x) => x.id === linkedId) ?? null) : null;
  const linkedDetail = useQuery({
    queryKey: ['curator', 'homework', 'one', linkedId],
    queryFn: () => progressApi.getStaffHomework(linkedId as string),
    enabled: !!linkedId && list.isSuccess && !inList,
    retry: false,
  });
  const linked = inList ?? (linkedId ? (linkedDetail.data ?? null) : null);
  const shown = active ?? linked;
  const dropLink = () => {
    if (linkedId) {
      const next = new URLSearchParams(params);
      next.delete('submission');
      setParams(next, { replace: true });
    }
  };
  const closeReview = () => {
    setActive(null);
    dropLink();
  };

  const isMobile = useIsMobile();
  // Решённые в этом заходе — чтобы окно не вернулось к ним, пока список перечитывается (`nextInQueue`).
  const [doneIds, setDoneIds] = useState<ReadonlySet<string>>(() => new Set());
  // ⚠️ ПОСЛЕ РЕШЕНИЯ — СРАЗУ СЛЕДУЮЩАЯ РАБОТА (решение владельца 13.10.2026, docs/CURATOR-MOBILE-DESIGN.md §10.2):
  // возврат в список и новый выбор стоят двух лишних действий на каждую работу. Сначала было только на телефоне;
  // в тот же день владелец сказал «на компьютере — да», и правило стало одним для обеих раскладок.
  const onDecided = (hw: HomeworkSubmission) => {
    const done = new Set(doneIds).add(hw.id);
    setDoneIds(done);
    const next = nextInQueue(items, hw.id, done);
    if (!next) return closeReview();
    dropLink();
    setActive(next);
  };

  // ── Телефон (docs/CURATOR-MOBILE-DESIGN.md §4.6) ──────────────────────────
  if (isMobile) {
    const tabs = (['pending', 'revision', 'approved', 'rejected'] as HomeworkStatus[]).map((s) => ({
      key: s,
      label: STATUS_LABELS[s],
    }));
    return (
      <div className="space-y-3">
        <MTitle title={t('curatorHomework.title')} subtitle={t('curatorHomework.queueCount', { count: total })} />
        <SearchInput value={q} placeholder={t('curatorHomework.searchPlaceholder')} onSearch={setQ} />
        <MSegmented tabs={tabs} active={filter ?? 'pending'} onChange={setFilter} />
        {linkedId && linkedDetail.isError && (
          <p className="px-0.5 text-[13px] text-error">{t('curatorHomework.linkedMissing')}</p>
        )}
        {list.isLoading ? (
          <p className="px-0.5 text-[13px] text-on-surface-variant">{t('common.loading')}</p>
        ) : list.isError ? (
          <p className="px-0.5 text-[13px] text-error">{humanizeError(list.error)}</p>
        ) : !items.length ? (
          <MEmpty
            icon="task_alt"
            title={
              q
                ? t('curatorHomework.searchEmpty', { q })
                : filter === 'pending'
                  ? t('curatorHomework.emptyPending')
                  : t('curatorHomework.emptyCategory', { category: filter ? STATUS_LABELS[filter] : '' })
            }
          />
        ) : (
          <>
            <MBlock>
              {items.map((hw, i) => {
                const files = hw.files ?? [];
                const voice =
                  splitHomeworkContent(hw.content).audio.length > 0 ||
                  files.some((f) => isAudioFile(f.file_name || f.file_url));
                return (
                  <MRow
                    key={hw.id}
                    // У проверенной работы нажатие и есть «Изменить»: окно то же, решение заменит прежнее.
                    onClick={() => setActive(hw)}
                    leading={<MIcon icon={voice ? 'mic' : 'description'} tone={hw.status === 'pending' ? 'warm' : 'muted'} />}
                    title={hw.student_name || t('curatorHomework.unknownStudent')}
                    meta={[hw.lesson_title, hw.section_title].filter(Boolean).join(' · ') || t('curatorHomework.homeworkTitle')}
                    trailing={
                      <span className="flex flex-none flex-col items-end gap-1">
                        {hw.resubmitted && <MTag tone="warm">{t('curatorHomework.resubmitted')}</MTag>}
                        <span className="flex items-center gap-1 text-[10.5px] tabular-nums text-on-surface-variant">
                          {files.length > 0 && (
                            <>
                              <span className="material-symbols-outlined text-[13px]">attach_file</span>
                              {files.length}
                            </>
                          )}
                          {new Date(hw.submitted_at).toLocaleString('ru-RU', {
                            day: '2-digit',
                            month: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      </span>
                    }
                    last={i === items.length - 1}
                  />
                );
              })}
            </MBlock>
            <div className="flex items-center justify-between gap-3 px-0.5 text-[12px] text-on-surface-variant">
              <span>{t('curatorHomework.shownOf', { shown: items.length, total })}</span>
              {list.hasNextPage && (
                <Button size="md" variant="ghost" loading={list.isFetchingNextPage} onClick={() => list.fetchNextPage()}>
                  {t('curatorHomework.showMore')}
                </Button>
              )}
            </div>
          </>
        )}
        {shown && <ReviewModal key={shown.id} hw={shown} onClose={closeReview} onDecided={() => onDecided(shown)} />}
      </div>
    );
  }

  return (
    <>
      <PageHeader title={t('curatorHomework.title')} subtitle={t('curatorHomework.queueCount', { count: total })} />
      <SearchInput
        className="mb-4"
        value={q}
        placeholder={t('curatorHomework.searchPlaceholder')}
        onSearch={setQ}
      />
      {linkedId && linkedDetail.isError && (
        <Card variant="subtle" className="mb-4 text-sm text-error">{t('curatorHomework.linkedMissing')}</Card>
      )}
      <div className="mb-6 flex flex-wrap gap-2">
        {(['pending', 'revision', 'approved', 'rejected'] as HomeworkStatus[]).map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`rounded-pill px-4 py-1.5 text-xs font-semibold uppercase tracking-label ${filter === s ? 'bg-primary text-on-primary' : 'bg-surface-container-lowest text-on-surface-variant ghost-border'}`}
          >
            {STATUS_LABELS[s]}
          </button>
        ))}
      </div>

      {list.isLoading ? (
        <Card variant="subtle">{t('common.loading')}</Card>
      ) : list.isError ? (
        <Card variant="subtle" className="text-center text-error">{humanizeError(list.error)}</Card>
      ) : !items.length ? (
        <Card variant="subtle" className="text-center text-on-surface-variant">
          {q
            ? t('curatorHomework.searchEmpty', { q })
            : filter === 'pending'
              ? t('curatorHomework.emptyPending')
              : t('curatorHomework.emptyCategory', { category: filter ? STATUS_LABELS[filter] : '' })}
        </Card>
      ) : (
        <>
        <ul className="space-y-3">
          {items.map((hw) => (
            <li key={hw.id}>
              <Card interactive onClick={() => setActive(hw)}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <CardTitle>{hw.lesson_title || hw.section_title || t('curatorHomework.homeworkTitle')}</CardTitle>
                    <p className="mt-1 flex items-center gap-1.5 text-sm font-medium text-on-surface">
                      <span className="material-symbols-outlined text-base text-on-surface-variant">person</span>
                      {hw.student_name || t('curatorHomework.unknownStudent')}
                    </p>
                    {hw.section_title && hw.lesson_title && (
                      <p className="mt-0.5 truncate text-xs text-on-surface-variant">{hw.section_title}</p>
                    )}
                  </div>
                  <div className="flex flex-none flex-col items-end gap-1.5">
                    <Pill variant={statusVariant(hw.status)}>{STATUS_LABELS[hw.status]}</Pill>
                    {/* Вторая попытка после «на доработку» или «отклонено» (договор
                        homework-resubmit.md §2): куратор читает её не как новую работу. */}
                    {hw.resubmitted && <Pill variant="primary">{t('curatorHomework.resubmitted')}</Pill>}
                    {/* ⚠️ КНОПКА У ПРОВЕРЕННОЙ РАБОТЫ. Карточка кликалась и раньше,
                        и разбор открывался для любого статуса — но об этом нельзя
                        было догадаться: ни кнопки, ни подписи. Куратор, поставивший
                        не ту оценку, не видел способа её исправить. Возможность
                        была, доступности не было. */}
                    {hw.status !== 'pending' && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActive(hw);
                        }}
                        className="inline-flex items-center gap-1 rounded-pill bg-surface-container-highest px-3 py-1 text-[11px] font-semibold text-on-surface-variant transition hover:bg-primary hover:text-on-primary"
                      >
                        <span className="material-symbols-outlined text-[14px]">edit</span>
                        {t('curatorHomework.editReview')}
                      </button>
                    )}
                  </div>
                </div>
                {hw.content && <HomeworkPreview content={hw.content} />}
                <p className="mt-2 flex items-center gap-1.5 text-xs text-on-surface-variant">
                  <span className="material-symbols-outlined text-sm">schedule</span>
                  {new Date(hw.submitted_at).toLocaleString('ru-RU')}
                  {hw.files && hw.files.length > 0 && (
                    <span className="ml-2 flex items-center gap-1">
                      <span className="material-symbols-outlined text-sm">attach_file</span>
                      {hw.files.length}
                    </span>
                  )}
                </p>
              </Card>
            </li>
          ))}
        </ul>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-on-surface-variant">
          <span>{t('curatorHomework.shownOf', { shown: items.length, total })}</span>
          {list.hasNextPage && (
            <Button size="sm" variant="ghost" loading={list.isFetchingNextPage} onClick={() => list.fetchNextPage()}>
              {t('curatorHomework.showMore')}
            </Button>
          )}
        </div>
        </>
      )}

      {shown && <ReviewModal key={shown.id} hw={shown} onClose={closeReview} onDecided={() => onDecided(shown)} />}
    </>
  );
}

/**
 * Окно проверки домашней работы.
 *
 * ⚠️ БЕЗ ОЦЕНКИ И ОЧКОВ (D15, решение владельца 10.09.2026). За домашку очки не
 * начисляются ни в одном режиме курса: куратор принимает, отклоняет или
 * возвращает на доработку, комментарий — по желанию. Утром того же дня здесь
 * было поле «Оценка (0–100)» и кнопка «Принять (+100)»; сервер после D15 число
 * игнорирует, и поле обещало бы то, чего не происходит. Экспортировано ради
 * рендер-проверки `homeworkReviewModal.test.ts`.
 */
export function ReviewModal({
  hw,
  onClose,
  onDecided,
}: {
  hw: HomeworkSubmission;
  onClose: () => void;
  /** После решения — вместо закрытия: страница откроет следующую работу (§10.2). Нет — окно закрывается. */
  onDecided?: () => void;
}) {
  const { t } = useTranslation();
  const STATUS_LABELS = useStatusLabels();
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  const [note, setNote] = useState(hw.curator_note ?? '');
  const [err, setErr] = useState<string | null>(null);

  // Записи — и из текста сдачи, и из вложений; сервер пока пишет их по-разному.
  const body = splitHomeworkContent(hw.content);
  const files = hw.files ?? [];
  const recordings = [
    ...body.audio,
    ...files.filter((f) => isAudioFile(f.file_name || f.file_url)).map((f) => f.file_url),
  ].filter((url, i, all) => all.indexOf(url) === i);
  const otherFiles = files.filter((f) => !isAudioFile(f.file_name || f.file_url));

  const review = useMutation({
    mutationFn: (status: HomeworkStatus) =>
      progressApi.reviewHomework(hw.id, { status, note }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['curator', 'homework'] });
      if (onDecided) onDecided();
      else onClose();
    },
    onError: (e: unknown) => setErr(humanizeError(e)),
  });

  // Три решения. На телефоне — прибитой полосой внизу окна во весь экран (§4.6, правило 2): «Отклонить» красным
  // контуром, «Принять» — единственное оранжевое. На компьютере — прежний ряд под комментарием.
  const decisions = isMobile ? (
    <div className="grid grid-cols-3 gap-2">
      <Button
        variant="ghost"
        className="border border-error px-2 text-[13px] text-error"
        onClick={() => review.mutate('rejected')}
        loading={review.isPending}
      >
        {t('curatorHomework.reject')}
      </Button>
      <Button variant="secondary" className="px-2 text-[13px]" onClick={() => review.mutate('revision')} loading={review.isPending}>
        {t('curatorHomework.sendRevision')}
      </Button>
      <Button className="px-2 text-[13px]" onClick={() => review.mutate('approved')} loading={review.isPending}>
        {t('curatorHomework.approve')}
      </Button>
    </div>
  ) : null;

  return (
    <Modal
      open
      onClose={onClose}
      title={hw.lesson_title || hw.section_title || t('curatorHomework.reviewTitle')}
      size="lg"
      phoneFull
      footer={decisions}
    >
      <div className="space-y-3">
        {/* Who + which lesson — context the curator needs before grading. */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="flex items-center gap-1.5 text-sm font-medium text-on-surface">
            <span className="material-symbols-outlined text-base text-on-surface-variant">person</span>
            {hw.student_name || t('curatorHomework.unknownStudent')}
          </span>
          <Pill variant={statusVariant(hw.status)}>{STATUS_LABELS[hw.status]}</Pill>
          {hw.resubmitted && <Pill variant="primary">{t('curatorHomework.resubmitted')}</Pill>}
          {/* ⚠️ ПОДСКАЗКА ПЕРЕПИСЫВАЛАСЬ ДВАЖДЫ 10.09.2026. Сначала стояло «Уже
              одобрено — очки повторно не начислятся» — стало ложным, когда
              оценку начали пересчитывать на каждой проверке. Затем «новая оценка
              заменит прежнюю, очки пересчитаются» — стало ложным с D15, когда
              очков за домашку не стало вовсе. Теперь только о решении. */}
          {hw.reviewed_at && (
            <span className="text-xs text-on-surface-variant">{t('curatorHomework.alreadyReviewedHint')}</span>
          )}
        </div>
        {/* Текст — только если ученик что-то написал. Запись голоса из урока
            приходит ссылкой прямо в тексте (1357 сдач из 2169 — только ссылка):
            её вынимаем и играем плеером, а не показываем голым адресом.
            data-selectable — в приложении выделение снято со всей страницы (`html.native body`), а текст ученика
            куратор копирует в отзыв (13.10.2026). На сайте он выделялся и так. */}
        {(body.text || recordings.length === 0) && (
          <Card variant="subtle">
            <p data-selectable className="whitespace-pre-wrap text-sm text-on-surface">{body.text || t('curatorHomework.emptyContent')}</p>
          </Card>
        )}
        {/* Голосовой ответ играется прямо здесь, «скачать» — мелко под плеером.
            Не играется (webm на старом Safari) — плеер покажет ошибку с
            «Повторить» и той же ссылкой, пустого места не будет. */}
        {recordings.map((url) => (
          <AudioPlayer key={url} src={url} downloadUrl={url} />
        ))}
        {otherFiles.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {otherFiles.map((f) => (
              <a key={f.id} href={f.file_url} target="_blank" rel="noreferrer">
                <Pill variant="neutral">{f.file_name}</Pill>
              </a>
            ))}
          </div>
        )}

        <Input
          label={t('curatorHomework.curatorComment')}
          placeholder={t('curatorHomework.curatorCommentPlaceholder')}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />

        {err && <p className="text-xs text-error">{err}</p>}

        {!isMobile && (
        <div className="flex flex-wrap justify-end gap-2 pt-2">
          <Button variant="danger" onClick={() => review.mutate('rejected')} loading={review.isPending}>
            {t('curatorHomework.reject')}
          </Button>
          <Button variant="secondary" onClick={() => review.mutate('revision')} loading={review.isPending}>
            {t('curatorHomework.sendRevision')}
          </Button>
          <Button onClick={() => review.mutate('approved')} loading={review.isPending}>
            {t('curatorHomework.approve')}
          </Button>
        </div>
        )}
      </div>
    </Modal>
  );
}
